import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, Globe, Send, User, AtSign, MessageSquare, LifeBuoy, Briefcase, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import PublicFooter from '../../components/PublicFooter';
import { contactAPI, handleApiError } from '../../utils/api';

const ContactPage = () => {
  const { t, currentLanguage } = usePublicLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'support',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [contactEmail, setContactEmail] = useState('hello@candio.ai');
  const inputBaseClass =
    'mt-2 w-full rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-100';

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await contactAPI.getInfo();
        const data = response.data;
        if (data.email) {
          setContactEmail(data.email);
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };
    fetchContactInfo();
  }, []);

  const subjectOptions = [
    {
      value: 'support',
      label: t('contact.subjects.support.label'),
      description: t('contact.subjects.support.description'),
      icon: LifeBuoy
    },
    {
      value: 'sales',
      label: t('contact.subjects.sales.label'),
      description: t('contact.subjects.sales.description'),
      icon: Briefcase
    },
    {
      value: 'general',
      label: t('contact.subjects.general.label'),
      description: t('contact.subjects.general.description'),
      icon: MessageCircle
    }
  ];
  const subjectHelp = subjectOptions.find((option) => option.value === formData.subject)?.description;

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = t('contact.form.errors.nameRequired');
    if (!formData.email.trim()) next.email = t('contact.form.errors.emailRequired');
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = t('contact.form.errors.emailInvalid');
    }
    if (!formData.message.trim()) next.message = t('contact.form.errors.messageRequired');
    if (formData.message.trim().length > 0 && formData.message.trim().length < 10) {
      next.message = t('contact.form.errors.messageTooShort');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t('contact.form.errors.formValidation'));
      return;
    }

    setLoading(true);
    try {
      await contactAPI.send({ ...formData, language: currentLanguage });
      toast.success(t('contact.form.success'));
      setFormData({ name: '', email: '', subject: 'support', message: '' });
    } catch (error) {
      const message = handleApiError(error, t('contact.form.errors.sendError'));
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 contact-handwritten-page">
      <section className="relative overflow-hidden contact-hero-bandboard contact-hero-whiteboard">
        <div className="hero-whiteboard-grid" aria-hidden="true" />
        <div className="hero-whiteboard-glare" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center process-arrow-sticker mb-6">
              {t('contact.title')}
            </span>
            <h1 className="contact-hero-title text-4xl md:text-6xl font-bold max-w-3xl mx-auto">{t('contact.subtitle')}</h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 contact-content">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-7 border border-gray-200 contact-form-paper">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 contact-form-title">{t('contact.form.title')}</h2>
              <p className="text-sm text-gray-600 mb-6 contact-form-subtitle">{t('contact.form.subtitle')}</p>

              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="text-sm font-medium text-gray-700 contact-form-label">{t('contact.form.name')}</label>
                    <div className="relative">
                      <User className="h-4 w-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none contact-hand-icon" />
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`${inputBaseClass} pl-11 ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''}`}
                        placeholder={t('contact.form.namePlaceholder')}
                      />
                    </div>
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="text-sm font-medium text-gray-700 contact-form-label">{t('contact.form.email')}</label>
                    <div className="relative">
                      <AtSign className="h-4 w-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none contact-hand-icon" />
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`${inputBaseClass} pl-11 ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''}`}
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                    </div>
                    {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <fieldset>
                    <legend className="text-sm font-medium text-gray-700 contact-form-label">{t('contact.form.subject')}</legend>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {subjectOptions.map((option) => {
                        const Icon = option.icon;
                        const selected = formData.subject === option.value;
                        return (
                          <label
                            key={option.value}
                            className={`group cursor-pointer rounded-xl border p-3.5 transition-all duration-200 ${
                              selected
                                ? 'border-primary-500 bg-primary-50 shadow-sm'
                                : 'border-gray-200 bg-gray-50/70 hover:border-primary-300 hover:bg-white'
                            }`}
                          >
                            <input
                              type="radio"
                              name="subject"
                              value={option.value}
                              checked={selected}
                              onChange={handleChange}
                              className="sr-only"
                            />
                            <div className="flex items-start gap-2.5">
                              <span
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${
                                  selected ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'
                                }`}
                              >
                                <Icon className="h-4 w-4 contact-hand-icon" />
                              </span>
                              <span className="min-w-0">
                                <span className={`block text-sm font-semibold ${selected ? 'text-primary-800' : 'text-gray-800'}`}>
                                  {option.label}
                                </span>
                                <span className="block text-xs text-gray-500 truncate">
                                  {option.description}
                                </span>
                              </span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-500" />
                    <p className="text-xs text-gray-600">{subjectHelp}</p>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="text-sm font-medium text-gray-700 contact-form-label">{t('contact.form.message')}</label>
                  <div className="relative">
                    <MessageSquare className="h-4 w-4 text-gray-400 absolute left-3.5 top-4 pointer-events-none contact-hand-icon" />
                    <textarea
                      id="contact-message"
                      name="message"
                      rows="6"
                      maxLength={2000}
                      value={formData.message}
                      onChange={handleChange}
                      className={`${inputBaseClass} pl-11 resize-y min-h-[144px] ${errors.message ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : ''}`}
                      placeholder={t('contact.form.messagePlaceholder')}
                    />
                    <div className="absolute right-3 bottom-3 text-[11px] text-gray-400 bg-white/90 rounded px-1.5 py-0.5">
                      {formData.message.length}/2000
                    </div>
                  </div>
                  {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center px-5 py-3.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  {loading ? t('contact.form.sending') : t('contact.form.send')}
                  <Send className="h-4 w-4 ml-2 contact-hand-icon" />
                </button>
              </form>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 contact-why-paper">
              <h3 className="text-base font-semibold text-gray-900 mb-4 contact-why-sticker inline-flex items-center">{t('contact.why.title')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: t('contact.why.demo.title'), desc: t('contact.why.demo.description') },
                  { title: t('contact.why.support.title'), desc: t('contact.why.support.description') },
                  { title: t('contact.why.pricing.title'), desc: t('contact.why.pricing.description') },
                  { title: t('contact.why.training.title'), desc: t('contact.why.training.description') }
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-lg border border-gray-200 bg-gray-50 contact-why-item">
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 contact-side-paper">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 contact-side-title">{t('contact.info.title')}</h3>
              <div className="text-sm text-gray-600 space-y-3">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-primary-600 contact-hand-icon" />
                  <span>{contactEmail}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-primary-600 contact-hand-icon" />
                  <span>{t('contact.info.hours.value')}</span>
                </div>
                <div className="flex items-center">
                  <Globe className="h-4 w-4 mr-2 text-primary-600 contact-hand-icon" />
                  <span>{t('contact.info.multilingual.value')}</span>
                </div>
                <p className="text-xs text-gray-500">{t('contact.info.responseTime')}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 contact-side-paper">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 contact-side-title">{t('contact.response.title')}</h3>
              <p className="text-xs text-gray-500 mb-4">{t('contact.response.subtitle')}</p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span>{t('contact.response.support')}</span>
                  <span className="text-primary-600 font-semibold">&lt; 24h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>{t('contact.response.sales')}</span>
                  <span className="text-primary-600 font-semibold">&lt; 4h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
